/**
 * process-graph.ts — pure compilation between the editor's ordered-steps form
 * and the process graph DSL.
 *
 * The editor authors a process as an ordered list of steps, where each step
 * is a single node or a parallel group of nodes. A node is either an
 * agent_executor (carrying a StepConfig) or a dynamic step (runtime-planned;
 * optional derivation goal). Compilation produces nodes/edges with the
 * respond node fixed at the end:
 *
 *   - every node in step i has an edge to every node in step i+1
 *   - every node in the last step has an edge to the respond node
 *   - zero steps compile to a respond-only graph (the Simple shape)
 *
 * Decompilation is the strict inverse: a graph round-trips into steps only if
 * its edge set is exactly the layered form above. Anything else (arbitrary
 * DAGs authored via the API) returns null and the editor shows the
 * graph as read-only JSON instead of the steps form.
 *
 * No React imports — unit-testable pure functions.
 */

import type {
  ProcessGraph,
  GraphNode,
  GraphEdge,
  StepConfig,
  DynamicStepProperties,
} from '@localagent/sdk';

export const RESPOND_NODE_ID = 'respond';

/**
 * A respond-only process has no agent_executor or dynamic nodes — its graph
 * is just the respond terminal. Those are the only processes that can run on
 * a workspace-less session (web chats): executor/dynamic nodes execute via
 * Reasoning.ExecuteGraph, which requires a workspace. Mirrors the backend predicate in
 * go/internal/sessions/database_agent_process.go.
 */
export function isRespondOnly(graph: ProcessGraph): boolean {
  return graph.nodes.every((n) => n.type === 'respond');
}

export type EditorStepNode =
  | { kind: 'agent'; label: string; config: StepConfig }
  | { kind: 'dynamic'; label: string; taskPrompt: string };

export function humanizeStepId(id: string, fallbackIndex: number): string {
  const match = /^step-(\d+)([a-z]?)$/.exec(id);
  if (match) {
    const num = match[1];
    const suffix = match[2] ? ` (${match[2]})` : '';
    return `Step ${num}${suffix}`;
  }
  const trimmed = id.trim();
  if (!trimmed) return `Step ${fallbackIndex + 1}`;
  const spaced = trimmed.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function nodeIdFor(stepIndex: number, nodeIndex: number, groupSize: number): string {
  if (groupSize === 1) return `step-${stepIndex + 1}`;
  const suffix = nodeIndex < 26 ? String.fromCharCode(97 + nodeIndex) : `-${nodeIndex + 1}`;
  return `step-${stepIndex + 1}${suffix}`;
}

export interface CompileOptions {
  respondLabel?: string;
}

export function compileStepsToGraph(
  steps: EditorStepNode[][],
  options?: CompileOptions,
): ProcessGraph {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const layers: string[][] = [];

  steps.forEach((group, stepIndex) => {
    const ids: string[] = [];
    group.forEach((step, nodeIndex) => {
      const id = nodeIdFor(stepIndex, nodeIndex, group.length);
      const label = (step.label ?? '').trim() || undefined;
      if (step.kind === 'agent') {
        const node: GraphNode = { id, type: 'agent_executor', properties: step.config };
        if (label) node.label = label;
        nodes.push(node);
      } else {
        const properties: DynamicStepProperties | undefined = step.taskPrompt.trim()
          ? { task_prompt: step.taskPrompt }
          : undefined;
        const node: GraphNode = properties
          ? { id, type: 'dynamic', properties }
          : { id, type: 'dynamic' };
        if (label) node.label = label;
        nodes.push(node);
      }
      ids.push(id);
    });
    layers.push(ids);
  });

  const respondNode: GraphNode = { id: RESPOND_NODE_ID, type: 'respond' };
  if (options?.respondLabel?.trim()) {
    respondNode.label = options.respondLabel.trim();
  }
  nodes.push(respondNode);

  for (let i = 0; i + 1 < layers.length; i++) {
    for (const from of layers[i]) {
      for (const to of layers[i + 1]) {
        edges.push({ from_node: from, to_node: to });
      }
    }
  }
  if (layers.length > 0) {
    for (const from of layers[layers.length - 1]) {
      edges.push({ from_node: from, to_node: RESPOND_NODE_ID });
    }
  }

  return { nodes, edges };
}

function edgeKey(e: { from_node: string; to_node: string }): string {
  return `${e.from_node} ${e.to_node}`;
}

function toEditorStepNode(n: GraphNode, fallbackIndex: number): EditorStepNode | null {
  const label = n.label ?? humanizeStepId(n.id, fallbackIndex);
  if (n.type === 'dynamic') {
    const props = (n.properties ?? {}) as DynamicStepProperties;
    return { kind: 'dynamic', label, taskPrompt: props.task_prompt ?? '' };
  }
  if (n.type === 'agent_executor') {
    if (!n.properties) return null;
    return { kind: 'agent', label, config: n.properties as StepConfig };
  }
  return null;
}

export interface DecompiledGraph {
  steps: EditorStepNode[][];
  respondLabel: string;
}

export function graphToSteps(graph: ProcessGraph): DecompiledGraph | null {
  const respondNodes = graph.nodes.filter((n) => n.type === 'respond');
  if (respondNodes.length !== 1) return null;

  const respondNode = respondNodes[0];
  const execNodes = graph.nodes.filter((n) => n.type !== 'respond');

  let nodeIndex = 0;
  for (const n of execNodes) {
    if (toEditorStepNode(n, nodeIndex++) === null) return null;
  }

  if (execNodes.length === 0) {
    return graph.edges.length === 0
      ? { steps: [], respondLabel: respondNode.label ?? 'Respond' }
      : null;
  }

  const execIds = new Set(execNodes.map((n) => n.id));
  const respondId = respondNode.id;

  const preds = new Map<string, string[]>();
  for (const n of execNodes) preds.set(n.id, []);
  for (const e of graph.edges) {
    if (e.to_node === respondId) {
      if (!execIds.has(e.from_node)) return null;
      continue;
    }
    if (!execIds.has(e.from_node) || !execIds.has(e.to_node)) return null;
    preds.get(e.to_node)!.push(e.from_node);
  }

  const depth = new Map<string, number>();
  for (const n of execNodes) depth.set(n.id, 0);
  let changed = true;
  let iterations = 0;
  while (changed) {
    if (iterations++ > execNodes.length + 1) return null; // cycle
    changed = false;
    for (const n of execNodes) {
      const d = Math.max(0, ...preds.get(n.id)!.map((p) => depth.get(p)! + 1));
      if (d !== depth.get(n.id)) {
        depth.set(n.id, d);
        changed = true;
      }
    }
  }

  const maxDepth = Math.max(...[...depth.values()]);
  const layers: GraphNode[][] = Array.from({ length: maxDepth + 1 }, () => []);
  for (const n of execNodes) layers[depth.get(n.id)!].push(n);

  const expected = new Set<string>();
  for (let i = 0; i + 1 < layers.length; i++) {
    for (const from of layers[i]) {
      for (const to of layers[i + 1]) {
        expected.add(edgeKey({ from_node: from.id, to_node: to.id }));
      }
    }
  }
  for (const from of layers[layers.length - 1]) {
    expected.add(edgeKey({ from_node: from.id, to_node: respondId }));
  }

  const actual = new Set(graph.edges.map(edgeKey));
  if (actual.size !== expected.size) return null;
  for (const key of actual) {
    if (!expected.has(key)) return null;
  }

  let flatIndex = 0;
  const steps = layers.map((layer) =>
    layer.map((n) => toEditorStepNode(n, flatIndex++)!),
  );

  return { steps, respondLabel: respondNode.label ?? 'Respond' };
}
